import React, { useState } from "react";
import "./App.css";
import Login from "./Login";

// Component Imports for Preschool Management
import PreschoolDashboard from "./components/PreschoolDashboard";
import NewAdmission from "./components/NewAdmission";
import StudentDirectory from "./components/StudentDirectory";
import AttendanceTracker from "./components/AttendanceTracker";
import ClassManagement from "./components/ClassManagement";
import FeeManagement from "./components/FeeManagement";
import StaffDirectory from "./components/StaffDirectory";
import ParentCommunication from "./components/ParentCommunication";
import StudentEnquiryComponent from "./components/StudentEnquiryComponent";
import Reports from "./components/Reports";
import ActivitiesModule from "./components/ActivitiesModule";
import TalkingSketchbookGame from "./components/games/TalkingSketch";
import NumberTracingGame from "./components/games/NumberTracingGame";
import AlphabetTracingGame from "./components/games/AlphabetTracingGame";
import LowercaseAlphabetTracingGame from "./components/games/LowercaseAlphabetTracingGame";
import AlphabetNameLearner from "./components/games/AlphabetNameLearner";
import PhonicsAlphabetLearning from "./components/games/PhonicsAlphabetLearning";
import EnglishAlphabetAscentLearner from "./components/games/EnglishAlphabetAscentLearner";
import CountAndMatchGame from "./components/games/numeracy/CountAndMatchGame";
import CountAndWriteGame from "./components/games/numeracy/CountAndWriteGame";
import CountAndColorBeadsGame from "./components/games/numeracy/CountAndColorBeadsGame";

// Icon Imports
import {
  FaHome,
  FaUserPlus,
  FaGraduationCap,
  FaCalendarCheck,
  FaChalkboardTeacher,
  FaMoneyBillWave,
  FaUsersCog,
  FaComments,
  FaQuestionCircle,
  FaChartBar,
  FaSignOutAlt,
  FaShapes,
  FaPaintBrush,
  FaPencilAlt,
  FaVolumeUp,
  FaGlobeAmericas,
  FaCalculator,
  FaPen
} from "react-icons/fa";

/**
 * Menu Configuration for Preschool ERP Solution
 * Roles: "1" = Admin / Principal, "2" = Teacher / Staff, "3" = Student / Interactive
 */
const MENU_ITEMS = [
  // --- Core & Admin Operations ---
  { key: "dashboard", label: "Dashboard", icon: FaHome, roles: ["1", "2"] },
  { key: "admissions", label: "New Admission", icon: FaUserPlus, roles: ["1", "2"] },
  { key: "students", label: "Student Directory", icon: FaGraduationCap, roles: ["1", "2"] },
  { key: "attendance", label: "Attendance", icon: FaCalendarCheck, roles: ["1", "2"] },
  { key: "activities", label: "Activities", icon: FaShapes, roles: ["1", "2"] },
  { key: "classes", label: "Classes & Batches", icon: FaChalkboardTeacher, roles: ["1"] },
  { key: "enquiry", label: "Admission Enquiries", icon: FaQuestionCircle, roles: ["1", "2"] },
  { key: "fees", label: "Fee Management", icon: FaMoneyBillWave, roles: ["1"] },
  { key: "communication", label: "Parent Broadcast", icon: FaComments, roles: ["1", "2"] },
  { key: "staff", label: "Staff Directory", icon: FaUsersCog, roles: ["1"] },
  { key: "reports", label: "ERP Reports", icon: FaChartBar, roles: ["1"] },
  
  // --- Interactive Educational Games & Learning Modules ---
  { key: "sketchgame", label: "Talking Sketch", icon: FaPaintBrush, roles: ["1", "2", "3"] },
  { key: "numbertracing", label: "Number Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "alphabettracing", label: "Alphabet Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "lowercasetracing", label: "Lowercase Tracing", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "spellingstudio", label: "Spelling Studio", icon: FaPencilAlt, roles: ["1", "2", "3"] },
  { key: "phonicslearning", label: "Phonics Sounds", icon: FaVolumeUp, roles: ["1", "2", "3"] },
  { key: "accentlearner", label: "Global Alphabets", icon: FaGlobeAmericas, roles: ["1", "2", "3"] },
  { key: "countandmatch", label: "Count & Match", icon: FaCalculator, roles: ["1", "2", "3"] },
  { key: "countandwrite", label: "Count & Write", icon: FaPen, roles: ["1", "2", "3"] },
  { key: "countandcolourbeads", label: "Count & Colour Beads", icon: FaPen, roles: ["1", "2", "3"] }

];

function App() {
  const [token, setToken] = useState(sessionStorage.getItem("token") || null);

  // Dynamic Preschool / School Name loaded from storage
  const [clientName, setClientName] = useState(
    sessionStorage.getItem("clientName") || "Wisdom Preschool"
  );

  // Client ID loaded dynamically from storage
  const [clientId, setClientId] = useState(
    sessionStorage.getItem("clientId") || "PRESCHOOL_01"
  );

  // Safely initialize roleId, ignoring literal "null" or "undefined" strings
  const [roleId, setRoleId] = useState(() => {
    const savedRole = sessionStorage.getItem("roleId");
    return savedRole && savedRole !== "undefined" && savedRole !== "null"
      ? savedRole
      : null;
  });

  // Current active navigation page
  const [page, setPage] = useState("dashboard");

  // Login Handler
  const handleLoginSuccess = (userToken, userRoleId, userClientName, userClientId) => {
    if (!userToken) {
      console.error("Login succeeded but no token was provided!");
      return;
    }

    sessionStorage.setItem("token", userToken);
    setToken(userToken);

    if (userClientName) {
      sessionStorage.setItem("clientName", userClientName);
      setClientName(userClientName);
    }

    if (userClientId) {
      sessionStorage.setItem("clientId", userClientId);
      setClientId(userClientId);
    }

    if (userRoleId !== undefined && userRoleId !== null) {
      const normalizedRole = String(userRoleId);
      sessionStorage.setItem("roleId", normalizedRole);
      setRoleId(normalizedRole);
    } else {
      sessionStorage.removeItem("roleId");
      setRoleId(null);
    }

    setPage("dashboard");
  };

  // Logout Handler
  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("roleId");
    sessionStorage.removeItem("clientName");
    sessionStorage.removeItem("clientId");

    setToken(null);
    setRoleId(null);
    setClientName("Wisdom Preschool");
    setPage("dashboard");
  };

  // Render Login view if unauthenticated
  if (!token || token === "undefined" || token === "null") {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Filter navigation menus dynamically based on user roleId
  const getFilteredMenuItems = () => {
    return MENU_ITEMS.filter((item) => {
      if (!roleId) return true; // Default fallthrough if no role set
      return item.roles.includes(String(roleId));
    });
  };

  return (
    <div style={styles.appContainer}>
      {/* ===== LEFT SIDEBAR (Pill-Button Menu Style) ===== */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>{clientName}</h2>
        </div>

        <nav style={styles.sidebarNav}>
          {getFilteredMenuItems().map((item) => {
            const IconComponent = item.icon;
            const isActive = page === item.key;
            return (
              <button
                key={item.key}
                style={{
                  ...styles.menuButton,
                  ...(isActive ? styles.menuButtonActive : styles.menuButtonInactive)
                }}
                onClick={() => setPage(item.key)}
              >
                <IconComponent style={styles.menuIcon} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div style={styles.sidebarFooter}>
          <button style={styles.logoutButton} onClick={handleLogout}>
            <FaSignOutAlt style={styles.menuIcon} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ===== RIGHT CONTENT AREA (Flexible Workspace) ===== */}
      <main style={styles.mainContent}>
        <div style={styles.dashboardContentWrapper}>
          {page === "dashboard" && (
            <PreschoolDashboard
              token={token}
              clientId={clientId}
              roleId={roleId}
              onNavigate={(targetPage) => setPage(targetPage)}
            />
          )}

          {page === "admissions" && (
            <NewAdmission token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "students" && (
            <StudentDirectory token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "attendance" && (
            <AttendanceTracker token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "activities" && (
            <ActivitiesModule token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "classes" && (
            <ClassManagement token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "enquiry" && (
            <StudentEnquiryComponent clientId={clientId} onBack={() => setPage("dashboard")} />
          )}

          {page === "fees" && (
            <FeeManagement token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "communication" && (
            <ParentCommunication token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "staff" && (
            <StaffDirectory token={token} onBack={() => setPage("dashboard")} />
          )}

          {page === "reports" && (
            <Reports token={token} onBack={() => setPage("dashboard")} />
          )}

          {/* Interactive Educational Games & Learning Modules */}
          {page === "sketchgame" && <TalkingSketchbookGame />}
          {page === "numbertracing" && <NumberTracingGame />}
          {page === "alphabettracing" && <AlphabetTracingGame />}
          {page === "lowercasetracing" && <LowercaseAlphabetTracingGame />}
          {page === "spellingstudio" && <AlphabetNameLearner />}
          {page === "phonicslearning" && <PhonicsAlphabetLearning />}
          {page === "accentlearner" && <EnglishAlphabetAscentLearner />}
          {page === "countandmatch" && <CountAndMatchGame />}
          {page === "countandwrite" && <CountAndWriteGame />}
          {page === "countandcolourbeads" && <CountAndColorBeadsGame />}

        </div>
      </main>
    </div>
  );
}

const styles = {
  appContainer: {
    display: "flex",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    backgroundColor: "#F1F5F9",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  sidebar: {
    width: "30%",
    minWidth: "280px",
    maxWidth: "340px",
    height: "100vh",
    backgroundColor: "#111827",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box"
  },
  sidebarHeader: {
    padding: "24px 20px 16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px"
  },
  homeLabel: {
    color: "#9CA3AF",
    fontSize: "0.95rem",
    fontWeight: "500",
    cursor: "pointer"
  },
  sidebarTitle: {
    margin: 0,
    color: "#FFFFFF",
    fontSize: "1.45rem",
    fontWeight: "700",
    letterSpacing: "-0.2px"
  },
  sidebarNav: {
    flex: 1,
    overflowY: "auto",
    padding: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    scrollBehavior: "smooth"
  },
  menuButton: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    width: "100%",
    padding: "14px 18px",
    borderRadius: "12px",
    fontSize: "1.02rem",
    fontWeight: "500",
    cursor: "pointer",
    textAlign: "left",
    border: "none",
    transition: "background-color 0.15s ease"
  },
  menuButtonInactive: {
    backgroundColor: "#1F2937",
    color: "#F9FAFB"
  },
  menuButtonActive: {
    backgroundColor: "#2563EB",
    color: "#FFFFFF"
  },
  menuIcon: {
    fontSize: "1.15rem",
    minWidth: "20px",
    textAlign: "center"
  },
  sidebarFooter: {
    padding: "16px",
    backgroundColor: "#111827"
  },
  logoutButton: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "12px 16px",
    backgroundColor: "#EF4444",
    border: "none",
    borderRadius: "12px",
    color: "#FFFFFF",
    fontSize: "1.0rem",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "left"
  },
  mainContent: {
    flex: 1,
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "#F8FAFC",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column"
  },
  dashboardContentWrapper: {
    padding: "24px",
    width: "100%",
    boxSizing: "border-box",
    flex: 1
  }
};

export default App;